import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';

export default function ShipmentPlanView() {
  const { id } = useParams<{ id: string }>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!id) {
        setError('Thiếu mã báo cáo');
        setLoading(false);
        return;
      }
      try {
        const { data: sessionData, error: sessErr } = await supabase.auth.getSession();
        if (sessErr) throw sessErr;
        const accessToken = sessionData.session?.access_token;
        if (!accessToken) {
          setError('Bạn cần đăng nhập để xem báo cáo này.');
          setLoading(false);
          return;
        }

        const url = `${SUPABASE_URL}/functions/v1/view-shipment-plan?id=${encodeURIComponent(id)}`;
        const res = await fetch(url, {
          method: 'GET',
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (!res.ok) {
          let msg = `Tải báo cáo thất bại (${res.status})`;
          try {
            const errBody = await res.json();
            if (errBody?.error) msg = errBody.error;
          } catch (_) { /* ignore */ }
          throw new Error(msg);
        }

        // Re-wrap into a blob with an explicit text/html type so the iframe
        // renders the markup instead of treating it as plain text.
        const text = await res.text();
        const htmlBlob = new Blob([text], { type: 'text/html;charset=utf-8' });
        const objectUrl = URL.createObjectURL(htmlBlob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        blobUrlRef.current = objectUrl;
        setBlobUrl(objectUrl);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-muted-foreground">Đang tải báo cáo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <p className="text-lg font-semibold text-destructive">Không thể mở báo cáo</p>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={blobUrl ?? undefined}
      title="Shipment Plan"
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
      style={{ width: '100vw', height: '100vh', border: 0, display: 'block' }}
    />
  );
}
